import moment from 'moment';

import { TimeFormat } from '@feathr/hooks';
import { isWretchError, type TConstraints, wretch } from '@feathr/rachis';

import { DisplayCampaign } from './display';
import type {
  IGoogleAdsGrantsFields,
  IGoogleAdsLocationSuggestion,
  IGoogleAdsSuggestProps,
  TGoogleAdsSuggestions,
} from './types';

/**
 * Longest business name Google will accept. It is shown above the display URL as an
 * ad's advertiser name, and Google rejects anything longer.
 *
 * @see https://support.google.com/google-ads/answer/12437745
 */
export const MAX_BUSINESS_NAME_LENGTH = 25;

/**
 * Shared base for Google Ad Grants campaigns. Smart and Search campaigns carry
 * an identical field set, validation, and suggestion API on the backend and
 * differ only by `_cls`, so both concrete classes extend this base and override
 * only `getDefaults` to stamp their own class string.
 */
export abstract class GoogleAdsCampaign<
  T extends IGoogleAdsGrantsFields,
> extends DisplayCampaign<T> {
  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      destination_url: {
        url: {
          message: '^Website must be a valid URL.',
        },
        presence: {
          allowEmpty: false,
          message: "^Website can't be empty.",
        },
      },
      business_name: {
        presence: {
          allowEmpty: false,
          message: "^Business name can't be empty.",
        },
        length: {
          maximum: MAX_BUSINESS_NAME_LENGTH,
          tooLong: `^Business name must be ${MAX_BUSINESS_NAME_LENGTH} characters or fewer.`,
        },
      },
      'exposure_settings.target_cap_daily': {
        presence: {
          message: '^Daily average budget cannot be empty.',
          allowEmpty: false,
        },
        numericality: {
          greaterThan: 0,
          lessThanOrEqualTo: 329,
          message: '^Daily average budget must be greater than $0 and less than or equal to $329.',
        },
      },
      /*
       * Override the default date_start constraints from baseConstraints
       */
      date_start: {
        presence: {
          allowEmpty: false,
          message: '^Start date cannot be empty.',
        },
      },
      // Override the default date_end constraints from baseConstraints
      date_end: {
        datetime: (value: string, attributes: any) => {
          if (attributes.date_start) {
            const dateStart = moment.utc(attributes.date_start, TimeFormat.isoDateTime);
            const dateEnd = moment.utc(value, TimeFormat.isoDateTime);
            const today = moment.utc('', TimeFormat.isoDateTime);
            const endStartDiff = dateEnd.diff(dateStart);
            if (dateEnd.isBefore(today)) {
              return {
                earliest: today.format(TimeFormat.isoDateTime),
                message: '^End date must not be in the past.',
              };
            }
            /*
             * Campaigns can end on the same day as they start
             * Invalid end dates are before start date and not on start date
             */
            if (dateEnd.isBefore(dateStart) && endStartDiff !== 0) {
              return {
                earliest: dateStart.format(TimeFormat.isoDateTime),
                message: '^End date must be on or after the start date.',
              };
            }
          }
          return true;
        },
        /*
         * Campaigns can run indefinitely to use up that free Google money
         * allowEmpty: true was not working and falling back to some invisible validation
         */
        presence: undefined,
      },
    };
  }

  public async suggest<S extends TGoogleAdsSuggestions>({
    type = 'ad',
    businessName,
    destinationUrl,
    keywordThemes,
  }: IGoogleAdsSuggestProps): Promise<S> {
    this.assertCollection(this.collection);

    const variant = `google.suggest.${type}` as const;
    const url = this.collection.url(variant, this.id);

    const response = await wretch<S>(url, {
      method: 'POST',
      body: JSON.stringify({
        business_name: businessName,
        destination_url: destinationUrl,
        keyword_themes: keywordThemes,
      }),
      headers: this.collection.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getLocations(searchTerm: string): Promise<IGoogleAdsLocationSuggestion[]> {
    this.assertCollection(this.collection);

    const url = this.collection.url('google.location', searchTerm);

    const response = await wretch<IGoogleAdsLocationSuggestion[]>(url, {
      method: 'GET',
      headers: this.collection.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
